import {
    IonBackButton,
    IonButtons,
    IonItem,
    IonRefresher,
    IonRefresherContent,
    IonText,
    RefresherEventDetail,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import classNames from 'classnames';
import { Form, Formik, FormikHelpers } from 'formik';
import { getDefaultStore, useAtom } from 'jotai';
import React, { useCallback, useMemo } from 'react';
import * as Yup from 'yup';

import LoadableContent from '../components/content/LoadableContent';
import DefaultTextarea from '../components/forms/defaulttextarea/DefaultTextarea';
import LoadingButton from '../components/loadingbutton/LoadingButton';
import Page from '../components/page/Page';
import PhotoUpload from '../components/photoUpload/PhotoUpload';
import { IJob } from '../interfaces/IJob';
import { IInstallationRouteProps } from '../interfaces/IRoute';
import {
    accessTokenAtom,
    extendedPastJobDetailsLoadableAtom,
    jobDetailsAtom,
} from '../store/Atoms';
import { docRequest, trackActionsRequest } from '../store/Requests';
import { ImageCategories } from '../types/ImageInfo';
import { MAX_IMAGE_NR } from '../utils/constants';
import { timestampToDate, timestampToTime } from '../utils/date';
import { getErrorMessage } from '../utils/errorMessage';
import { useCustomRouter } from '../utils/hooks/useCustomRouter';

const defaultStore = getDefaultStore();

const InstallerSchema = () =>
    Yup.object().shape({
        installerComment: Yup.string().required(t`Please enter comment`),
    });

const PastJobDetails: React.FC<IInstallationRouteProps> = ({ match }) => {
    const jobId = +match.params.jobId;
    const router = useCustomRouter();
    const [presentToast] = useIonToast();
    const [value, refresh] = useAtom(extendedPastJobDetailsLoadableAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const job = useMemo(
        () => (value.state === 'hasData' ? value.data : undefined),
        [value]
    );

    const handleRefresh = useCallback(
        async (event: CustomEvent<RefresherEventDetail>) => {
            try {
                await refresh();
            } catch (e: any) {
                presentToast({
                    message: t`Failed to refresh the page! Please try again.`,
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }
            event.detail.complete();
        },
        [presentToast, refresh]
    );

    const interventionHistory =
        job &&
        job.interventionsHistory &&
        job.interventionsHistory.find(
            (intervention) => intervention.interventionId === jobId
        );

    const installerComment =
        interventionHistory && interventionHistory.comment
            ? interventionHistory.comment
            : '';

    const jobEndDate = useMemo(
        () =>
            interventionHistory && interventionHistory.timeStop
                ? new Date(interventionHistory.timeStop).getTime()
                : false,
        [interventionHistory]
    );

    const numOfRooms = useMemo(() => (job ? job.rooms.length : 0), [job]);
    const numOfHeaters = useMemo(
        () =>
            job
                ? job.materialIds.filter(
                      (materialId) => materialId.typeProduct === 'DBOX'
                  ).length
                : 0,
        [job]
    );

    const handleFormSubmit = useCallback(
        async (
            data: { installerComment: string },
            actions: FormikHelpers<{ installerComment: string }>
        ) => {
            actions.setSubmitting(true);
            try {
                await docRequest(accessToken, jobId, {
                    installerComment: data.installerComment,
                    pastIntervention: true,
                });
                const updatedInterventionHistory =
                    job?.interventionsHistory.find(
                        (interventionHistoryJob) =>
                            interventionHistoryJob.interventionId === jobId
                    );
                if (updatedInterventionHistory) {
                    updatedInterventionHistory['comment'] =
                        data.installerComment;
                }
                const jobToSave: any = job as IJob;
                // Update past job atom with new comments
                defaultStore.set(jobDetailsAtom, jobToSave);
                trackActionsRequest('past_int_upd_inst_comm');
                router.push('/past-jobs', 'back');
            } catch (e: any) {
                await presentToast({
                    message: getErrorMessage(
                        e,
                        t`Oops, something went wrong while saving intervention information`
                    ),
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }
            actions.setSubmitting(false);
        },
        [accessToken, job, jobId, presentToast, router]
    );

    return (
        <Page
            title={t`Past Job Description`}
            ionButtons={
                <IonButtons slot="start">
                    <IonBackButton text={t`Back`} />
                </IonButtons>
            }
        >
            <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                <IonRefresherContent refreshingSpinner="circular"></IonRefresherContent>
            </IonRefresher>
            <LoadableContent value={value}>
                {job ? (
                    <>
                        <IonItem lines="full">
                            <IonText>
                                <Trans>End Date</Trans>:{' '}
                                {jobEndDate
                                    ? `${timestampToTime(
                                          jobEndDate
                                      )} ${timestampToDate(jobEndDate)}`
                                    : '-'}
                            </IonText>
                        </IonItem>
                        <IonItem lines="full">
                            <IonText>
                                <Trans>Customer name</Trans>:{' '}
                                {`${job.customerId[1] || ''}`}
                            </IonText>
                        </IonItem>
                        <IonItem lines="full">
                            <IonText>
                                <Trans>Address</Trans>:{' '}
                                {`${job.street || ''} ${job.number || ''}, ${
                                    job.street2 || ''
                                } ${job.city || ''}, ${job.zip || ''}`}
                            </IonText>
                        </IonItem>
                        <IonItem lines="full">
                            <IonText>
                                <Trans>Rooms already installed</Trans>:{' '}
                                {numOfRooms}
                            </IonText>
                        </IonItem>
                        <IonItem lines="full">
                            <IonText>
                                <Trans>Heaters already installed</Trans>:{' '}
                                {numOfHeaters}
                            </IonText>
                        </IonItem>
                        <Formik
                            initialValues={{
                                installerComment: installerComment,
                            }}
                            onSubmit={handleFormSubmit}
                            validationSchema={InstallerSchema}
                            validateOnMount={true}
                        >
                            {({
                                values,
                                handleChange,
                                isSubmitting,
                                isValid,
                                errors,
                                touched,
                            }) => (
                                <Form>
                                    <IonItem lines="full">
                                        <DefaultTextarea
                                            className={classNames({
                                                'ion-invalid':
                                                    errors.installerComment,
                                                'ion-touched':
                                                    touched.installerComment,
                                            })}
                                            name="installerComment"
                                            value={values.installerComment}
                                            label={t`Installer comment`}
                                            onIonInput={handleChange}
                                            errorText={errors.installerComment}
                                        ></DefaultTextarea>
                                    </IonItem>
                                    <div className="ion-padding-start ion-padding-end ion-margin-top ion-margin-bottom">
                                        <PhotoUpload
                                            className="ion-margin-bottom ion-margin-top"
                                            imageCode={
                                                ImageCategories.additionalPhotos
                                            }
                                            maxNumber={MAX_IMAGE_NR}
                                            label={t`Add Relevant Photo`}
                                            mandatory={true}
                                        />
                                    </div>
                                    <div className="ion-padding">
                                        <LoadingButton
                                            loading={isSubmitting}
                                            disabled={!isValid}
                                            color="medium"
                                            type="submit"
                                        >
                                            <Trans>Save</Trans>
                                        </LoadingButton>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </>
                ) : (
                    <p>
                        <Trans>Intervention with id {jobId} not found</Trans>
                    </p>
                )}
            </LoadableContent>
        </Page>
    );
};

export default PastJobDetails;
